<template>
  <b-modal
    v-model="dialogVisible"
    size="xl"
    centered
    scrollable
    hide-footer
    title="AI Description Search"
    modal-class="ai-search-modal"
    @hidden="resetState"
  >
    <div class="ai-shell">
      <div class="ai-search-panel">
        <b-input-group class="ai-search-input-group">
          <template #prepend>
            <span class="ai-search-icon" aria-hidden="true">
              <i class="fa fa-search"></i>
            </span>
          </template>

          <b-form-input
            ref="searchInput"
            v-model="searchQuery"
            class="ai-search-input"
            placeholder="Search an occasion, dish, or wine note..."
            @keyup.enter.prevent="handleSearch"
          />

          <template #append>
            <b-button
              variant="primary"
              class="ai-search-button"
              :disabled="isLoading"
              @click="handleSearch"
            >
              <span v-if="isLoading" class="ai-btn-loading">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Searching...
              </span>
              <span v-else>Search</span>
            </b-button>
          </template>
        </b-input-group>

        <div class="ai-helper-text">
          Search returns both direct database matches and Gemini AI recommendations.
        </div>

        <div v-if="statusMessage" class="ai-status-banner" :class="statusBannerClass">
          <div class="ai-status-banner__left">
            <span class="ai-status-badge" :class="statusBadgeClass">{{ statusBadgeText }}</span>
            <span class="ai-status-banner__message">{{ statusMessage }}</span>
          </div>

          <div class="ai-status-banner__meta">
            <span v-if="responseCode">{{ responseCode }}</span>
            <span v-if="responseTime !== null">{{ responseTime }} ms</span>
          </div>
        </div>

        <div v-if="aiRecommendations.length || pairingSummary" class="ai-recommendation-panel">
          <div class="ai-summary-title">Gemini Wine Recommendations</div>
          <div class="ai-recommendation-note">
            These are AI wine pairings for "{{ searchQuery }}". The inventory check happens below.
          </div>

          <ol class="ai-recommendation-list">
            <li
              v-for="item in aiRecommendations"
              :key="item"
              class="ai-recommendation-list__item"
            >
              <span class="ai-recommendation-list__badge">AI</span>
              <span class="ai-recommendation-list__text">{{ item }}</span>
            </li>
          </ol>

          <div v-if="pairingSummary" class="ai-pairing-summary">
            {{ pairingSummary }}
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="ai-state-box">
        <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
        <div class="mt-2">Searching DB &amp; AI...</div>
      </div>

      <div v-else-if="hasSearched && !results.length" class="ai-state-box ai-state-empty">
        No inventory matches found for "{{ searchQuery }}". The Gemini recommendations are shown above.
      </div>

      <template v-else-if="results.length">
        <div class="ai-summary-bar">
          <div class="ai-summary-title">Results Summary</div>
          <div class="ai-summary-pills">
            <span class="ai-pill">Total Rows: {{ results.length }}</span>
            <span class="ai-pill ai-pill--db">DB Count: {{ dbCount }}</span>
            <span class="ai-pill ai-pill--ai">AI Count: {{ aiCount }}</span>
          </div>
        </div>

        <div class="table-responsive ai-results-wrapper">
          <table class="table ai-results-table table-hover align-middle">
            <thead>
              <tr>
                <th class="ai-select-column">Select</th>
                <th>SKU</th>
                <th>Name</th>
                <th>Store Location</th>
                <th class="text-end">Price</th>
                <th>Winery</th>
                <th>Country</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in results" :key="item._searchKey">
                <td class="ai-select-column">
                  <b-form-checkbox
                    v-model="selectedResultKeys"
                    :value="item._searchKey"
                    :aria-label="`Select ${item.name || item.sku}`"
                  />
                </td>
                <td>{{ item.sku }}</td>
                <td>
                  <div class="ai-name-cell">
                    <div class="ai-name-main">{{ item.name }}</div>
                    <div v-if="item.ai_matched_keyword" class="ai-name-subtitle">
                      Matched via: {{ item.ai_matched_keyword }}
                    </div>
                  </div>
                </td>
                <td>{{ item.store_location }}</td>
                <td class="text-end">{{ formatPrice(item.price) }}</td>
                <td>{{ item.winery }}</td>
                <td>{{ item.country }}</td>
                <td>
                  <span
                    class="ai-source-badge"
                    :class="item.match_source === 'ai' ? 'ai-source-badge--ai' : 'ai-source-badge--db'"
                  >
                    {{ item.match_source === 'ai' ? 'AI Recommended' : 'DB Direct' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ai-create-so-bar">
          <span v-if="selectedResultKeys.length" class="ai-selected-count">
            {{ selectedResultKeys.length }} item(s) selected
          </span>
          <b-button
            variant="primary"
            class="bdr-rad"
            :disabled="!selectedResultKeys.length"
            @click="createSalesOrder"
          >
            Create SO
          </b-button>
        </div>
      </template>

      <div v-if="errorMessage" class="ai-state-box ai-state-error">
        {{ errorMessage }}
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="outline-primary" class="bdr-rad" @click="closeModal">
          Close
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AiDescriptionSearchModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    endpoint: {
      type: String,
      default: '/index.php?r=api/dinventory/ai-description-search',
    },
    accessToken: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      searchQuery: '',
      isLoading: false,
      hasSearched: false,
      results: [],
      selectedResultKeys: [],
      aiRecommendations: [],
      dbCount: 0,
      aiCount: 0,
      pairingSummary: '',
      responseCode: '',
      responseTime: null,
      errorMessage: '',
      rawResponse: null,
      responseStatus: 'idle',
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(nextValue) {
        this.$emit('input', nextValue);
      },
    },
    statusMessage() {
      if (this.responseStatus === 'success') {
        return 'API Request Successful';
      }

      if (this.responseStatus === 'error') {
        return this.errorMessage || 'API Request Failed';
      }

      return '';
    },
    statusBannerClass() {
      return this.responseStatus === 'success'
        ? 'ai-status-banner--success'
        : this.responseStatus === 'error'
          ? 'ai-status-banner--error'
          : '';
    },
    statusBadgeClass() {
      return this.responseStatus === 'success'
        ? 'ai-status-badge--success'
        : 'ai-status-badge--error';
    },
    statusBadgeText() {
      return this.responseStatus === 'success'
        ? '✅ API Request Successful'
        : '❌ API Request Failed';
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput && this.$refs.searchInput.focus) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
  },
  methods: {
    closeModal() {
      this.dialogVisible = false;
    },
    resetState() {
      this.searchQuery = '';
      this.isLoading = false;
      this.hasSearched = false;
      this.results = [];
      this.selectedResultKeys = [];
      this.aiRecommendations = [];
      this.dbCount = 0;
      this.aiCount = 0;
      this.pairingSummary = '';
      this.responseCode = '';
      this.responseTime = null;
      this.errorMessage = '';
      this.rawResponse = null;
      this.responseStatus = 'idle';
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return value;
      }

      return numericValue.toFixed(2);
    },
    normalizeRows(payload) {
      const list = Array.isArray(payload)
        ? payload
        : payload && Array.isArray(payload.data)
          ? payload.data
          : payload && Array.isArray(payload.all_data)
            ? payload.all_data
            : payload && Array.isArray(payload.results)
              ? payload.results
              : payload && Array.isArray(payload.items)
                ? payload.items
                : [];

      return list.map((item, index) => ({
        id: item.id !== undefined && item.id !== null ? item.id : index + 1,
        sku: item.sku !== undefined && item.sku !== null ? item.sku : (item.SKU !== undefined && item.SKU !== null ? item.SKU : ''),
        name: item.name !== undefined && item.name !== null ? item.name : (item.Name !== undefined && item.Name !== null ? item.Name : ''),
        store_location: item.store_location !== undefined && item.store_location !== null
          ? item.store_location
          : (item.Location !== undefined && item.Location !== null ? item.Location : (item.StoreLocation !== undefined && item.StoreLocation !== null ? item.StoreLocation : '')),
        price: item.price !== undefined && item.price !== null ? item.price : (item.Price !== undefined && item.Price !== null ? item.Price : ''),
        winery: item.winery !== undefined && item.winery !== null ? item.winery : (item.Winery !== undefined && item.Winery !== null ? item.Winery : ''),
        country: item.country !== undefined && item.country !== null ? item.country : (item.Country !== undefined && item.Country !== null ? item.Country : ''),
        match_source: item.match_source !== undefined && item.match_source !== null ? String(item.match_source) : (item.is_ai === true ? 'ai' : 'direct'),
        is_ai: item.is_ai === true || item.match_source === 'ai',
        ai_matched_keyword: item.ai_matched_keyword !== undefined && item.ai_matched_keyword !== null ? item.ai_matched_keyword : '',
        product_id: item.id_product !== undefined && item.id_product !== null
          ? item.id_product
          : (item.product_id !== undefined && item.product_id !== null ? item.product_id : ''),
        _searchKey: item.id !== undefined && item.id !== null
          ? item.id
          : (item.sku !== undefined && item.sku !== null
            ? item.sku
            : (item.SKU !== undefined && item.SKU !== null ? item.SKU : `${index}-${(item.name !== undefined && item.name !== null ? item.name : (item.Name !== undefined && item.Name !== null ? item.Name : 'row'))}`)),
      }));
    },
    normalizeRecommendationList(payload) {
      const list = Array.isArray(payload) ? payload : [];

      return list
        .map(function (item) {
          return String(item || '').trim();
        })
        .filter(function (item) {
          return item !== '';
        });
    },
    async handleSearch() {
      const query = String(this.searchQuery || '').trim();

      this.hasSearched = true;
      this.errorMessage = '';
      this.results = [];
      this.selectedResultKeys = [];
      this.aiRecommendations = [];
      this.dbCount = 0;
      this.aiCount = 0;
      this.pairingSummary = '';
      this.responseCode = '';
      this.responseTime = null;
      this.rawResponse = null;
      this.responseStatus = 'idle';

      if (!query) {
        this.responseStatus = 'error';
        this.errorMessage = 'Please enter a search term.';
        this.responseCode = '400 Bad Request';
        this.rawResponse = { status: 'error', message: this.errorMessage };
        return;
      }

      const startedAt = performance.now();
      this.isLoading = true;

      console.log('%c🚀 [API Call Initiated]', 'color:#0d6efd;font-weight:bold;', {
        endpoint: this.endpoint,
        query,
      });

      try {
        const config = {
          params: { query: query },
          headers: {
            'Content-Type': 'application/json',
          },
        };

        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }

        const response = await axios.get(this.endpoint, config);
        const { data, status, statusText } = response;

        this.responseTime = Math.round(performance.now() - startedAt);
        this.responseCode = `${status} ${statusText || 'OK'}`;
        this.rawResponse = data;

        const isSuccess = status === 200 && data && data.status === 'success';
        if (isSuccess) {
          this.responseStatus = 'success';
          this.dbCount = Number(
            data.db_count !== undefined && data.db_count !== null
              ? data.db_count
              : (data.direct_count !== undefined && data.direct_count !== null ? data.direct_count : 0)
          );
          this.aiCount = Number(data.ai_count !== undefined && data.ai_count !== null ? data.ai_count : 0);
          this.pairingSummary = data.pairing_summary || '';
          this.aiRecommendations = this.normalizeRecommendationList(
            data.ai_recommendations || data.ai_keywords || data.ai_styles || []
          );
          this.results = this.normalizeRows(data.data || data.all_data || data.direct_data || data.results || data.items);

          console.log('%c✅ [API Call Success]', 'color:#198754;font-weight:bold;', {
            status: this.responseCode,
            duration: this.responseTime,
            payload: data,
          });
        } else {
          this.responseStatus = 'error';
          this.errorMessage = (data && data.message) || 'Unexpected response from server.';
          this.results = [];
          this.aiRecommendations = [];
          this.pairingSummary = '';

          console.log('%c❌ [API Call Error]', 'color:#dc3545;font-weight:bold;', {
            status: this.responseCode,
            duration: this.responseTime,
            payload: data,
          });
        }
      } catch (error) {
        const response = error.response || {};
        const data = response.data || {};

        this.responseTime = Math.round(performance.now() - startedAt);
        this.responseStatus = 'error';
        this.responseCode = response.status
          ? `${response.status} ${response.statusText || ''}`.trim()
          : 'Network Error';
        this.errorMessage = data.message || error.message || 'Unable to fetch AI search results.';
        this.rawResponse = data || { status: 'error', message: this.errorMessage };
        this.aiRecommendations = [];
        this.pairingSummary = '';

        console.log('%c❌ [API Call Error]', 'color:#dc3545;font-weight:bold;', error.response || error);
      } finally {
        this.isLoading = false;
      }
    },
    createSalesOrder() {
      const selectedProductIds = this.results
        .filter((item) => this.selectedResultKeys.includes(item._searchKey) && item.product_id)
        .map((item) => String(item.product_id));
      const productIds = Array.from(new Set(selectedProductIds));

      if (!productIds.length) {
        return;
      }

      this.dialogVisible = false;
      this.$router.push({
        path: '/dsales/create',
        query: { product_ids: productIds.join(',') },
      });
    },
  },
};
</script>

<style scoped>
.ai-shell {
  padding: 4px 2px 2px;
}

.ai-search-panel {
  background: linear-gradient(180deg, #f7fbff 0%, #edf5ff 100%);
  border: 1px solid #d7e7ff;
  border-radius: 18px;
  padding: 18px;
}

.ai-search-input-group {
  box-shadow: 0 8px 24px rgba(13, 110, 253, 0.08);
}

.ai-search-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  min-width: 46px;
  border: 1px solid #c6dbff;
  border-right: 0;
  border-radius: 14px 0 0 14px;
  background: #fff;
  color: #0d6efd;
}

.ai-search-input {
  border-left: 0;
  border-right: 0;
  min-height: 48px;
}

.ai-search-button {
  min-width: 120px;
  border-radius: 0 14px 14px 0;
}

.ai-btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ai-helper-text {
  margin-top: 10px;
  font-size: 12px;
  color: #54708f;
}

.ai-status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #d9e6fb;
  background: #fff;
}

.ai-status-banner--success {
  background: #f2fbf5;
  border-color: #c8ebd1;
}

.ai-status-banner--error {
  background: #fff5f5;
  border-color: #f2c0c0;
}

.ai-status-banner__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ai-status-banner__message {
  font-size: 13px;
  font-weight: 700;
  color: #1f3552;
}

.ai-status-banner__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  color: #5b7090;
  font-size: 12px;
  font-weight: 600;
}

.ai-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
}

.ai-status-badge--success {
  color: #146c43;
  background: #daf4df;
  border: 1px solid #bfe8c7;
}

.ai-status-badge--error {
  color: #b42318;
  background: #fde8e7;
  border: 1px solid #f1b4b0;
}

.ai-state-box {
  margin-top: 18px;
  padding: 20px;
  border-radius: 14px;
  border: 1px dashed #c8d9f2;
  background: #fff;
  text-align: center;
  color: #41566f;
}

.ai-state-empty {
  color: #576b83;
}

.ai-state-error {
  color: #b42318;
  border-color: #f1b4b0;
  background: #fff6f6;
}

.ai-summary-bar {
  margin-top: 18px;
  padding: 12px 14px;
  border: 1px solid #d7e7ff;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, #f6faff 0%, #edf5ff 100%);
}

.ai-summary-title {
  font-size: 13px;
  font-weight: 800;
  color: #1f3552;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.ai-recommendation-panel {
  margin-top: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #d7e7ff;
  background: #ffffff;
}

.ai-recommendation-note {
  margin-top: 6px;
  font-size: 12px;
  color: #54708f;
}

.ai-recommendation-list {
  margin: 12px 0 0;
  padding-left: 20px;
}

.ai-recommendation-list__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  color: #1f3552;
  font-size: 13px;
  line-height: 1.5;
}

.ai-recommendation-list__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #eef4ff;
  border: 1px solid #c9d8ff;
  color: #0d6efd;
  font-size: 11px;
  font-weight: 800;
  flex: 0 0 auto;
}

.ai-recommendation-list__text {
  flex: 1 1 auto;
}

.ai-pairing-summary {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f5f9ff;
  color: #22415f;
  font-size: 13px;
  line-height: 1.5;
}

.ai-select-column {
  width: 68px;
  text-align: center;
  white-space: nowrap;
}

.ai-create-so-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 12px;
}

.ai-selected-count {
  color: #54708f;
  font-size: 12px;
  font-weight: 600;
}

.ai-summary-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.ai-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #cfe0ff;
  background: #fff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.ai-pill--db {
  color: #18794e;
  background: #eef9f1;
  border-color: #cbeed6;
}

.ai-pill--ai {
  color: #0d6efd;
  background: #eef4ff;
  border-color: #c9d8ff;
}

.ai-results-wrapper {
  border: 1px solid #d7e7ff;
  border-top: 0;
  border-radius: 0 0 16px 16px;
  overflow: hidden;
  background: #fff;
}

.ai-results-table {
  margin-bottom: 0;
}

.ai-results-table thead th {
  background: #123d6b;
  color: #fff;
  border-bottom: 0;
  font-size: 13px;
  white-space: nowrap;
}

.ai-results-table tbody td {
  vertical-align: top;
  font-size: 13px;
}

.ai-name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ai-name-main {
  font-weight: 600;
  color: #1f2937;
}

.ai-name-subtitle {
  font-size: 12px;
  color: #54708f;
}

.ai-source-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.ai-source-badge--db {
  background: #eef1f5;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.ai-source-badge--ai {
  background: #eef4ff;
  color: #0d6efd;
  border: 1px solid #c9d8ff;
}

.bdr-rad {
  border-radius: 12px;
}

@media (max-width: 768px) {
  .ai-search-panel {
    padding: 14px;
    border-radius: 14px;
  }

  .ai-search-button {
    min-width: 94px;
    font-size: 13px;
  }

  .ai-search-icon {
    width: 40px;
    min-width: 40px;
  }

  .ai-status-banner {
    padding: 12px;
  }

  .ai-status-banner__message {
    width: 100%;
  }
}
</style>
